import React, { Component, useContext } from 'react';
import { computed, action, observable, reaction, runInAction, comparer } from 'mobx';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
}

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

const RESET_TOKEN = Symbol();
class ConfigMerger {
    constructor(map) {
        this.keyMergers = {};
        for (let [k, m] of Object.entries(map || {})) {
            this.defineMergeHandler(k, m);
        }
    }
    mergeConfigs(base, ...layers) {
        let newConfig = Object.assign({}, base);
        for (let layer of layers) {
            if (layer == null)
                continue;
            if (layer[RESET_TOKEN])
                newConfig = {};
            for (let [k, v] of Object.entries(layer)) {
                if (k == RESET_TOKEN)
                    continue;
                const merger = this.keyMergers[k];
                newConfig[k] = merger ? merger(newConfig[k], v) : v;
            }
        }
        return newConfig;
    }
    defineMergeHandler(key, merger) {
        const keys = Array.isArray(key) ? key : [key];
        for (let k of keys) {
            let normMerger;
            if (merger instanceof ConfigMerger) {
                normMerger = (base, layer) => merger.mergeConfigs(base, layer);
            }
            else {
                normMerger = merger;
            }
            this.keyMergers[k] = normMerger;
        }
    }
}
function merger(spec) {
    return new ConfigMerger(spec);
}
function addHiddenProp(object, propName, value) {
    Object.defineProperty(object, propName, {
        enumerable: false,
        writable: true,
        configurable: true,
        value
    });
}
function getInheritedHiddenProp(target, propName, defaultValue) {
    if (!Object.prototype.hasOwnProperty.call(target, propName)) {
        const inherited = target[propName] || defaultValue;
        let newValue;
        if (Array.isArray(defaultValue) || defaultValue == 'array') {
            newValue = [...inherited];
        }
        else if (defaultValue == 'set') {
            newValue = new Set(inherited);
        }
        else if (typeof defaultValue == 'object' || defaultValue == 'object') {
            newValue = Object.assign({}, inherited);
        }
        addHiddenProp(target, propName, newValue);
    }
    return target[propName];
}
class DescriptorCache {
    constructor(generator) {
        this.generator = generator;
        this._cache = {};
    }
    get(...opts) {
        let cacheString = '';
        for (let v of opts) {
            cacheString += `${v.toString()};`;
        }
        return this._cache[cacheString] || (this._cache[cacheString] = this.generator(...opts));
    }
}
function flattenRegexAlternatives(namedData) {
    const out = {};
    for (let [k, v] of Object.entries(namedData)) {
        if (v === undefined)
            continue;
        const m = k.match(/(.*?)(\d+)/);
        const sk = m[1];
        if (sk in out)
            throw `Regex key used multiple times: ${sk}`;
        out[sk] = v;
    }
    return out;
}
const mergeRefs = (...refs) => (ref) => {
    refs.forEach((resolvableRef) => {
        if (typeof resolvableRef === "function") {
            resolvableRef(ref);
        }
        else if (resolvableRef) {
            resolvableRef.current = ref;
        }
    });
};

function getFetcherParams(fetcher) {
    let params = fetcher.options.params;
    let resolvedParams;
    if (typeof params == 'string') {
        if (params in fetcher.owner) {
            params = fetcher.owner[params];
        }
        else {
            throw `Object does not have ${params} property`;
        }
    }
    if (params === undefined) {
        params = fetcher.owner[`${fetcher.propertyName}Params`];
    }
    if (typeof params == 'function') {
        resolvedParams = params.apply(fetcher.owner, [fetcher.owner, fetcher]);
    }
    else {
        resolvedParams = params;
    }
    if (fetcher['localParams']) {
        const local_params = fetcher['localParams'];
        for (let k of Object.keys(local_params)) {
            if (resolvedParams[k] !== undefined)
                resolvedParams[k] = local_params[k];
        }
    }
    return resolvedParams;
}

const defaultConfig = {
    build_parameters: getFetcherParams,
    defaults: {
        method: 'GET',
        missingTokens: 'skip',
        resolveTokens: ['this', '$react_props', '$route_params', '$token_context'],
    },
};
const axiosConfigMerger = merger({
    headers: merger(),
    params: merger(),
});
const apiConfigMerger = merger({
    token_context: merger(),
    defaults: axiosConfigMerger,
});

const configurationMerger = merger({
    api: apiConfigMerger,
});
const defaultConfig$1 = {
    api: defaultConfig,
    error_handler: console.error,
};

const dataDecoratorsSym = Symbol("Registered Data Decorators");
const dataManagerSym = Symbol("Data Manager");
const StoreFetcherSymbol = Symbol("Store Fetcher");
class FetchingManager {
    constructor(owner) {
        this.owner = owner;
        this.propFetchers = {};
        this._mounted = false;
        this._initialized = false;
        this.initializeInstance();
    }
    get fetchedProperties() {
        return Object.keys(this.propFetchers);
    }
    get allFetchers() {
        return Object.values(this.propFetchers);
    }
    get active() {
        return this._mounted;
    }
    get config() {
        return this._config;
    }
    goActive(config) {
        this._mounted = true;
        this._config = config || defaultConfig$1;
        for (let fetcher of Object.values(this.propFetchers)) {
            fetcher.goActive();
        }
    }
    goInactive() {
        this._mounted = false;
        for (let fetcher of Object.values(this.propFetchers)) {
            fetcher.goInactive();
        }
    }
    fetcherFor(prop) {
        return this.propFetchers[prop];
    }
    get fetcherParameters() {
        return this.owner[dataManagerSym];
    }
    initializeInstance() {
        if (this._initialized)
            return;
        const decorators = this.owner[dataDecoratorsSym];
        if (decorators) {
            // Build property key array from both strings and symbols
            const keys = [...Object.getOwnPropertySymbols(decorators), ...Object.keys(decorators)];
            for (const key of keys) {
                const d = decorators[key];
                const descriptor = d.descriptor;
                const initialValue = descriptor
                    ? descriptor.initializer
                        ? descriptor.initializer.call(this.owner)
                        : descriptor.value
                    : undefined;
                const fetcher = new d.fetcher_class(this, d);
                this.propFetchers[d.fieldName] = fetcher;
                fetcher.initializeProperty(d.fieldName, initialValue);
            }
        }
        this.updateReferencedFetchers();
    }
    updateReferencedFetchers() {
        const storeDecorators = this.owner[StoreFetcherSymbol];
        if (storeDecorators) {
            for (let [k, v] of Object.entries(storeDecorators)) {
                const name = this.resolveName(v);
                if (!this.propFetchers[name])
                    throw new Error(`Could not find Fetcher "${name}"`);
                this.owner[k] = this.propFetchers[name];
            }
        }
    }
    resolveName(sdecl) {
        if (sdecl.name)
            return sdecl.name;
        if (typeof sdecl.property == 'string') {
            const v = sdecl.property;
            const removeTerms = ['_fetcher', 'Fetcher'];
            for (let term of removeTerms) {
                if (v.endsWith(term))
                    return v.slice(0, -term.length);
            }
        }
        throw new Error(`Could not assume Fetcher Name for ${sdecl.property.toString()}`);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], FetchingManager.prototype, "_mounted", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], FetchingManager.prototype, "active", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], FetchingManager.prototype, "goActive", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], FetchingManager.prototype, "goInactive", null);
function getFetchingManager(inst, create = true) {
    if (!inst[dataDecoratorsSym])
        return null;
    if (create && !inst[dataManagerSym]) {
        inst[dataManagerSym] = new FetchingManager(inst);
    }
    return inst[dataManagerSym];
}

const FetchingContext = React.createContext(defaultConfig$1);
const FetchingConfig = ({ config, children }) => {
    const inheritedContext = useContext(FetchingContext);
    const ctx = configurationMerger.mergeConfigs(inheritedContext, config);
    return React.createElement(FetchingContext.Provider, { value: ctx, children: children });
};
function fetching_component(WrappedComponent) {
    class FetchWrapped extends Component {
        constructor() {
            super(...arguments);
            this.componentRef = React.createRef();
            this.refCallback = mergeRefs(this.componentRef, this.props.forwardedRef);
        }
        componentDidMount() {
            this.fetchingManager.goActive(this.context);
        }
        componentWillUnmount() {
            this.fetchingManager.goInactive();
        }
        get fetchingManager() {
            return getFetchingManager(this.componentRef.current);
        }
        render() {
            const _a = this.props, rest = __rest(_a, ["forwardedRef"]);
            return React.createElement(WrappedComponent, Object.assign({ ref: this.refCallback }, rest));
        }
    }
    FetchWrapped.contextType = FetchingContext;
    return React.forwardRef((props, ref) => {
        return React.createElement(FetchWrapped, Object.assign({}, props, { forwardedRef: ref }));
    });
}

class AbstractFetcher {
    constructor(manager, options) {
        this.manager = manager;
        this.fetchingStatus = 'unloaded';
        this.valueStatus = 'unloaded';
        this.internalOptions = options;
        this.post_construct(options.parameters);
    }
    post_construct(options) { }
    get owner() { return this.manager.owner; }
    get config() { return this.manager.config; }
    get options() { return this.internalOptions.parameters; }
    get propertyName() { return this.internalOptions.fieldName; }
    initializeProperty(key, initialValue) {
        Object.defineProperty(this.owner, key, propDescriptors.get(key));
    }
}
__decorate([
    observable,
    __metadata("design:type", String)
], AbstractFetcher.prototype, "fetchingStatus", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], AbstractFetcher.prototype, "valueStatus", void 0);
__decorate([
    observable.ref,
    __metadata("design:type", Error)
], AbstractFetcher.prototype, "currentError", void 0);
function has_fetcher(fetcher_class, params) {
    const fetcherDecorator = (target, property, descriptor) => {
        const decorators = getInheritedHiddenProp(target, dataDecoratorsSym, {});
        decorators[property] = {
            fetcher_class,
            parameters: params,
            fieldName: property,
            descriptor,
        };
        return propInitializerDescriptors.get(property, true);
    };
    fetcherDecorator.fetcher_class = fetcher_class;
    fetcherDecorator.fetcher_params = params;
    return fetcherDecorator;
}
function getFetcher(target, key) {
    return getFetchingManager(target).fetcherFor(key);
}
function mark_fetcher_store(target, property, name) {
    const decorators = getInheritedHiddenProp(target, StoreFetcherSymbol, {});
    decorators[property] = {
        property,
        name,
    };
}
function fetcher_instance(target, property, descriptor) {
    if (property) {
        mark_fetcher_store(target, property);
        return descriptor;
    }
    else {
        const fetcher_name = target;
        return (target, property, descriptor) => {
            mark_fetcher_store(target, property, fetcher_name);
            return descriptor;
        };
    }
}
const propInitializerDescriptors = new DescriptorCache((prop, enumerable) => {
    return {
        configurable: true,
        enumerable: enumerable,
        get() {
            getFetchingManager(this);
            return this[prop];
        },
        set(value) {
            getFetchingManager(this);
            this[prop] = value;
        }
    };
});
const propDescriptors = new DescriptorCache((key) => {
    return {
        configurable: true,
        enumerable: true,
        get() {
            return getFetchingManager(this).fetcherFor(key).value;
        },
        set(v) {
            getFetchingManager(this).fetcherFor(key).value = v;
        }
    };
});

const ERROR_VALUE_WORKING = Symbol('ERROR_VALUE.WORKING');
const WORKING_VALUE_LAST = Symbol('WORKING_VALUE.LAST');
const WORKING_VALUE_UNDEF = Symbol('WORKING_VALUE.UNDEFINED');
const WorkingValue = {
    LAST: WORKING_VALUE_LAST,
    UNDEFINED: WORKING_VALUE_UNDEF,
};
const ErrorValue = {
    LAST: WORKING_VALUE_LAST,
    WORKING: ERROR_VALUE_WORKING,
    UNDEFINED: WORKING_VALUE_UNDEF,
};
class BaseTimedFetcher extends AbstractFetcher {
    constructor() {
        super(...arguments);
        this.active = false;
        this._accessed = false;
        this._throttleQueued = false;
        this._stateToken = 0;
    }
    post_construct(opts) {
        this._value = opts.initial;
        if (this._value !== undefined) {
            this.valueStatus = 'loaded';
        }
    }
    goActive() {
        if (this.disposer)
            return;
        if (this.hasGetterValue()) {
            this.valueStatus = 'loaded';
            return;
        }
        if ((!this.options.lazy || this._accessed) && this.manager.active) {
            this.active = true;
            if (this.options.poll) {
                this.pollingTimer = setInterval(() => {
                    this.requestThrottled();
                }, this.options.poll);
            }
            this.disposer = reaction(() => this.getTrackedObservable(), (params) => {
                this.lastTrackedValue = params;
                this.requestDebounced();
            }, {
                fireImmediately: (this._value === undefined),
            });
        }
    }
    goInactive() {
        if (this.disposer) {
            this.disposer();
            this.disposer = null;
        }
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
        }
        this.active = false;
    }
    needsToExecuteFetch() {
        return true;
    }
    requestDebounced() {
        if (this._debounceTimer)
            clearTimeout(this._debounceTimer);
        if (this.options.debounce !== false && this.options.debounce !== 0) {
            this._debounceTimer = setTimeout(() => this.requestThrottled(), this.options.debounce);
        }
        else {
            this.requestThrottled();
        }
    }
    requestThrottled() {
        if (this._throttleTimer) {
            this._throttleQueued = true;
        }
        else {
            this.doFetch();
            if (this.options.throttle) {
                this._throttleTimer = setTimeout(() => {
                    this._throttleTimer = null;
                    if (this._throttleQueued) {
                        this._throttleQueued = false;
                        this.doFetch();
                    }
                }, this.options.throttle);
            }
        }
    }
    updateValueStatus(s) {
        if (this.valueStatus == 'loaded')
            return;
        this.valueStatus = s;
    }
    async doFetch() {
        if (!this.disposer)
            return;
        if (!this.needsToExecuteFetch())
            return;
        const newStateToken = ++this._stateToken;
        runInAction(() => {
            this.fetchingStatus = 'loading';
            this.updateValueStatus('loading');
        });
        try {
            const data = await this.executeFetch(this.lastTrackedValue);
            if (newStateToken >= this._stateToken) {
                runInAction(() => {
                    this._value = data;
                    this.valueStatus = 'loaded';
                    if (newStateToken == this._stateToken) {
                        this.fetchingStatus = 'loaded';
                    }
                    this.currentError = null;
                });
            }
        }
        catch (e) {
            if (newStateToken == this._stateToken) {
                runInAction(() => {
                    this.fetchingStatus = 'error';
                    this.updateValueStatus('error');
                    this.currentError = e;
                    if (this.config.error_handler) {
                        this.config.error_handler(e);
                    }
                    else {
                        console.error(e);
                    }
                });
            }
        }
    }
    get getterValue() {
        if (!this.internalOptions.descriptor)
            return;
        const getter = this.internalOptions.descriptor.get;
        if (getter)
            return getter.call(this.owner);
    }
    hasGetterValue() {
        return this.getterValue != null;
    }
    get value() {
        if (this.hasGetterValue())
            return this.getterValue;
        if (!this._accessed) {
            this._accessed = true;
            requestAnimationFrame(() => this.goActive());
        }
        let working_action = undefinedDefault(this.options.workingValue, WorkingValue.LAST);
        let error_action = undefinedDefault(this.options.errorValue, ErrorValue.WORKING);
        if (error_action === ErrorValue.WORKING)
            error_action = working_action;
        if (error_action !== ErrorValue.LAST && this.fetchingStatus === 'error') {
            if (error_action === ErrorValue.UNDEFINED)
                return undefined;
            return error_action;
        }
        else if (working_action !== WorkingValue.LAST && this.fetchingStatus !== 'loaded') {
            if (working_action === WorkingValue.UNDEFINED)
                return undefined;
            return working_action;
        }
        return this._value;
    }
    set value(v) {
        if (this.internalOptions.descriptor && this.internalOptions.descriptor.set) {
            this.internalOptions.descriptor.set.call(this.owner, v);
        }
        this._value = v;
        this.valueStatus = 'loaded';
    }
}
__decorate([
    observable.ref,
    __metadata("design:type", Object)
], BaseTimedFetcher.prototype, "_value", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], BaseTimedFetcher.prototype, "active", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BaseTimedFetcher.prototype, "goActive", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], BaseTimedFetcher.prototype, "goInactive", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], BaseTimedFetcher.prototype, "getterValue", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [Object])
], BaseTimedFetcher.prototype, "value", null);
function undefinedDefault(value, defaultValue) {
    if (value === undefined)
        return defaultValue;
    return value;
}

class SimpleFetcher extends BaseTimedFetcher {
    constructor() {
        super(...arguments);
        this.isTriggerUnchanged = this.options.params_equal || comparer.default;
    }
    goActive() {
        if (typeof this.options.fetch != 'function')
            return;
        super.goActive();
    }
    get fetchParams() {
        return getFetcherParams(this);
    }
    getTrackedObservable() {
        return this.fetchParams;
    }
    executeFetch() {
        const fetch = this.options.fetch;
        return fetch.apply(this.owner, [
            Object.assign({}, this.fetchParams),
            {
                self: this.owner,
                config: this.config,
            }
        ]);
    }
}
__decorate([
    computed.struct,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], SimpleFetcher.prototype, "fetchParams", null);
function fetched(fetch, params) {
    return has_fetcher(SimpleFetcher, Object.assign(Object.assign({}, params), { fetch }));
}

const deep_get = (o, p, defaultValue = undefined) => {
    let obj = o;
    for (let key of p) {
        if (obj == null)
            return defaultValue;
        // if (obj == null || (isObservable(obj) && !mobxHas(obj, key)) || !(key in obj)) return defaultValue;
        if (key in obj) {
            obj = obj[key];
        }
        else {
            return defaultValue;
        }
    }
    return obj;
};

const URL_REGEX = /:(?:(?<token1>\w+)|\((?<token2>[\w\.]+)\))/g;
class ApiFetcher extends BaseTimedFetcher {
    constructor() {
        super(...arguments);
        this.localParams = {};
        this.lastResponse = null;
    }
    resolveOption(key) {
        return this.options[key] || (this.apiConfig.defaults && this.apiConfig.defaults[key]) || defaultConfig.defaults[key];
    }
    expandTokens(passedTokens) {
        const tokens = [];
        for (let t of passedTokens) {
            if (t == '$react_props')
                tokens.push('props');
            else if (t == '$react_state')
                tokens.push('state');
            else if (t == '$route_params')
                tokens.push('props.match.params');
            else
                tokens.push(t);
        }
        return tokens;
    }
    getTokenResolver() {
        const resolver = this.resolveOption('resolveTokens');
        if (!resolver) {
            return tok => undefined;
        }
        else if (typeof resolver != 'function') {
            const resolutionPaths = this.expandTokens(resolver);
            return tok => firstResolution(this.owner, resolutionPaths, tok, {
                '$params': this.fetchParams,
                '$token_context': this.apiConfig.token_context || {},
            });
        }
        else {
            return tok => resolver(tok, this.fetchParams, this.owner);
        }
    }
    buildUrl() {
        const url = this.options.url;
        const missingTokens = new Set();
        const resolveToken = this.getTokenResolver();
        const reqUrl = url.replace(URL_REGEX, (whole_token, ...rest) => {
            const kwgroups = flattenRegexAlternatives(rest[rest.length - 1]);
            const { token } = kwgroups;
            const tokValue = resolveToken(token);
            if (tokValue == null) {
                missingTokens.add(whole_token);
                return whole_token;
            }
            else {
                return tokValue.toString();
            }
        });
        if (missingTokens.size > 0) {
            let missingTokensStrategy = this.resolveOption('missingTokens');
            if (typeof missingTokensStrategy == 'function') {
                missingTokensStrategy = missingTokensStrategy([...missingTokens]);
            }
            if (missingTokensStrategy == 'error') {
                throw `Missing URL Parameters {${[...missingTokens].join(', ')}}`;
            }
            else if (missingTokensStrategy == 'skip') {
                return null;
            }
        }
        return reqUrl;
    }
    get fetchUrl() {
        return this.buildUrl();
    }
    get fetchParams() {
        if (this.apiConfig.build_parameters) {
            return this.apiConfig.build_parameters(this);
        }
        return {};
    }
    get apiConfig() {
        return this.config.api;
    }
    transformResponse(response) {
        const transformer = this.options.responseTransformer || this.apiConfig.transform_response;
        if (typeof transformer == 'string') {
            return response.data[transformer];
        }
        else if (transformer) {
            return transformer(response);
        }
        else {
            const segments = this.fetchUrl.split('/');
            const lastSegment = segments[segments.length - 1];
            if (lastSegment.startsWith(':')) {
                return response.data;
            }
            else {
                return response.data[lastSegment] || response.data;
            }
        }
    }
    async executeFetch(reqConfig) {
        if (!reqConfig)
            return null;
        const axios = this.apiConfig.backend;
        const response = await axios.request(reqConfig);
        runInAction(() => {
            this.lastResponse = response;
        });
        return this.transformResponse(response);
    }
    getTrackedObservable() {
        if (!this.fetchUrl)
            return null;
        return axiosConfigMerger.mergeConfigs(this.apiConfig.defaults, {
            url: this.fetchUrl,
            params: this.fetchParams,
        });
    }
}
__decorate([
    observable.shallow,
    __metadata("design:type", Object)
], ApiFetcher.prototype, "localParams", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ApiFetcher.prototype, "lastResponse", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], ApiFetcher.prototype, "fetchUrl", null);
__decorate([
    computed.struct,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], ApiFetcher.prototype, "fetchParams", null);
function api_fetched(url, a, b) {
    let transform;
    let options;
    if (a && typeof a != 'string' && typeof a != 'function') {
        options = a;
    }
    else {
        options = b;
        transform = a;
    }
    return has_fetcher(ApiFetcher, Object.assign({ url, responseTransformer: transform }, options));
}
function firstResolution(context, paths, key, specialPaths) {
    specialPaths = specialPaths || {};
    specialPaths['this'] = context;
    const keyBits = key.split('.');
    if (keyBits.length > 1) {
        let ctx = context;
        if (specialPaths[keyBits[0]]) {
            ctx = specialPaths[keyBits.shift()];
        }
        return deep_get(ctx, keyBits, undefined);
    }
    else {
        for (let path of paths) {
            let value;
            if (specialPaths[path]) {
                value = specialPaths[path][key];
            }
            else {
                const pathBits = [...path.split('.'), key];
                value = deep_get(context, pathBits, undefined);
            }
            if (value !== undefined)
                return value;
        }
    }
    return undefined;
}

export { getFetchingManager, FetchingManager, fetching_component, FetchingConfig, ConfigMerger, merger, RESET_TOKEN, defaultConfig$1 as config, configurationMerger, AbstractFetcher, getFetcher, has_fetcher, fetcher_instance, BaseTimedFetcher, WorkingValue, ErrorValue, SimpleFetcher, fetched, ApiFetcher, api_fetched };
//# sourceMappingURL=fetcher.module.js.map
